/**
 * Face Search ↔ Transfers — admin button for a transfer row / detail.
 *
 *   <FaceSearchTransferButton transferRef={transfer.id} />
 *
 * Shows "Enable face search" (one click creates a live gallery on the transfer's Drive folder and starts it —
 * link + QR work immediately, photos are indexed as the transfer uploads them), or, once enabled,
 * Copy link · QR · Open with a small status.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanFace, Copy, QrCode, ExternalLink, Loader2 } from "lucide-react";
import { faceAdmin, errMsg, saveBlob } from "../lib/faceApi";

export default function FaceSearchTransferButton({ transferRef, compact = false, onToast }) {
  const navigate = useNavigate();
  const [ev, setEv] = useState(undefined); // undefined = loading, null = none yet
  const [busy, setBusy] = useState(false);
  const toast = (msg, kind) => (onToast ? onToast(msg, kind) : kind === "error" ? window.alert(msg) : null);

  useEffect(() => {
    let alive = true;
    if (!transferRef) return undefined;
    faceAdmin.byTransfer(transferRef).then((e) => alive && setEv(e)).catch(() => alive && setEv(null));
    return () => { alive = false; };
  }, [transferRef]);

  const enable = async () => {
    setBusy(true);
    try {
      const created = await faceAdmin.fromTransfer(transferRef);
      setEv(created);
      toast("Face search is live — share the link or QR with guests", "success");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(ev.public_url); toast("Guest link copied", "success"); } catch (_) { window.prompt("Copy", ev.public_url); }
  };
  const qr = async () => {
    try { saveBlob(await faceAdmin.qr(ev.id), `${(ev.title || "gallery").replace(/[^a-z0-9]+/gi, "-")}-qr.png`); } catch (e) { toast(errMsg(e), "error"); }
  };

  const base = "inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-white/10 disabled:opacity-50";
  if (ev === undefined) return <span className="inline-flex items-center gap-1 text-xs text-white/40"><Loader2 className="h-3 w-3 animate-spin" /></span>;
  if (ev === null) {
    return (
      <button type="button" onClick={enable} disabled={busy} className={`${base} text-amber-200`} title="Create a face-search gallery on this transfer's Drive folder">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanFace className="h-3.5 w-3.5" />} Enable face search
      </button>
    );
  }
  const done = (ev.photos_indexed || 0);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {!compact && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
          <ScanFace className="h-3 w-3" /> Face search · {done}/{ev.photos_total || 0}
        </span>
      )}
      <button type="button" onClick={copy} className={base}><Copy className="h-3.5 w-3.5" /> Copy link</button>
      <button type="button" onClick={qr} className={base}><QrCode className="h-3.5 w-3.5" /> QR</button>
      <button type="button" onClick={() => navigate(`/face-search/${ev.id}`)} className={base}><ExternalLink className="h-3.5 w-3.5" /> Open</button>
    </span>
  );
}
